import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { Skeleton } from './ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Compass, PlaneTakeoff, ArrowRight, Loader2, MapPin, Search as SearchIcon, ChevronDown } from 'lucide-react';

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

// "Cheapest outbound sectors from <country>" explorer — all airports of a country to popular
// world destinations. One-way/round-trip toggle. Clicking a card opens the destination country's
// full airport list so travellers pick a specific city, then runs a real search.
export const FlightDiscovery = ({ currency = 'INR' }) => {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState('IN');
  const [originName, setOriginName] = useState('India');
  const [ctryOpen, setCtryOpen] = useState(false);
  const [ctryQuery, setCtryQuery] = useState('');
  const [ctryResults, setCtryResults] = useState([]);
  const [roundTrip, setRoundTrip] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null); // clicked discovery item
  const [airports, setAirports] = useState(null);

  useEffect(() => {
    if (!ctryOpen) return;
    let active = true;
    const t = setTimeout(() => {
      api.originCountries({ q: ctryQuery }).then(({ data }) => { if (active) setCtryResults(data.countries || []); }).catch(() => {});
    }, 150);
    return () => { active = false; clearTimeout(t); };
  }, [ctryOpen, ctryQuery]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.flightDiscovery({ origin_country: origin, currency, round_trip: roundTrip })
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData({ items: [], regions: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [origin, currency, roundTrip]);

  const runSearch = (originCode, destCode, date) => {
    const dep = date || todayPlus(30);
    const params = {
      origin: originCode, destination: destCode,
      trip_type: roundTrip ? 'round_trip' : 'one_way',
      depart_date: dep, return_date: roundTrip ? todayPlus(37) : '',
      adults: 1, children: 0, cabin: 'economy',
    };
    navigate(`/flights/results?${new URLSearchParams(params).toString()}`);
  };

  const openPicker = (it) => {
    setPicker(it); setAirports(null);
    api.countryAirports({ cc: it.destination_country_code, name: it.destination_country })
      .then(({ data }) => setAirports(data.airports || []))
      .catch(() => setAirports([]));
  };

  return (
    <section className="mt-6" data-testid="flight-discovery">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Compass className="h-5 w-5 text-accent" />
          <div>
            <h2 className="font-display text-xl font-semibold tracking-tight">Cheapest flights out of {data?.origin_country_name || 'India'}</h2>
            <p className="text-sm text-muted-foreground">Live lowest {roundTrip ? 'round-trip' : 'one-way'} fares from all airports to popular destinations worldwide.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* one-tap trip-type toggle */}
          <div className="inline-flex rounded-full bg-secondary/50 border border-border p-1" data-testid="discovery-trip-toggle">
            {[['ow', 'One-way', false], ['rt', 'Round trip', true]].map(([k, l, v]) => (
              <button key={k} onClick={() => setRoundTrip(v)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${roundTrip === v ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
                data-testid={`discovery-trip-${k}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground hidden sm:inline">From</span>
            <Popover open={ctryOpen} onOpenChange={(o) => { setCtryOpen(o); if (o) setCtryQuery(''); }}>
              <PopoverTrigger asChild>
                <button className="w-44 flex items-center justify-between gap-2 rounded-xl border border-input bg-card px-3 h-10 text-sm font-medium" data-testid="discovery-region-select">
                  <span className="truncate">{originName}</span><ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-0" data-testid="discovery-country-popover">
                <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                  <SearchIcon className="h-4 w-4 text-muted-foreground" />
                  <input autoFocus value={ctryQuery} onChange={(e) => setCtryQuery(e.target.value)} placeholder="Search any country…"
                    className="w-full bg-transparent outline-none text-sm" data-testid="discovery-country-search" />
                </div>
                <div className="max-h-64 overflow-auto py-1">
                  {ctryResults.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">No countries found</div>}
                  {ctryResults.map((c) => (
                    <button key={c.code} onClick={() => { setOrigin(c.code); setOriginName(c.name); setCtryOpen(false); }}
                      className={`w-full text-left px-3 py-2 hover:bg-secondary flex items-center justify-between ${c.code === origin ? 'bg-accent/5' : ''}`}
                      data-testid={`discovery-country-${c.code}`}>
                      <span className="text-sm font-medium truncate">{c.name}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{c.code}</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : (data?.items || []).length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="discovery-empty">
          No live sectors to show right now. Try another origin.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="discovery-grid">
          {data.items.map((it) => (
            <button key={it.destination_country_code + it.destination} type="button" onClick={() => openPicker(it)}
              className="group text-left rounded-2xl border border-border bg-card overflow-hidden card-elevate flex items-stretch focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none"
              data-testid={`discovery-sector-${it.destination_country_code}`}>
              <div className="w-24 shrink-0 overflow-hidden bg-secondary">
                {it.image && <img src={it.image} alt={it.destination_country} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = 'none'; }} />}
              </div>
              <div className="p-3 flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{it.destination_city}, {it.destination_country}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <PlaneTakeoff className="h-3 w-3" /> {it.origin_city} <ArrowRight className="h-3 w-3" /> {it.destination}
                </p>
                <div className="mt-1.5 flex items-end justify-between">
                  <div>
                    <span className="text-[10px] text-muted-foreground">from · {roundTrip ? 'round trip' : 'one way'}</span>
                    <p className="text-base font-semibold tabular text-accent leading-none">{formatPrice(it.price_inr, currency)}</p>
                  </div>
                  <span className="text-xs font-medium text-accent inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-[gap]">Explore <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Deep-dive: pick a specific city/airport in the destination country */}
      <Dialog open={!!picker} onOpenChange={(o) => { if (!o) setPicker(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="discovery-airport-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-accent" /> Choose a city in {picker?.destination_country}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Flying from {picker?.origin_city} ({picker?.origin}) · {roundTrip ? 'round trip' : 'one way'}. Pick a destination airport.</p>
          {airports === null ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
          ) : airports.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No airports found for this country.</p>
          ) : (
            <div className="max-h-80 overflow-auto -mx-2 px-2 grid gap-1" data-testid="discovery-airport-list">
              {picker && (
                <button onClick={() => { runSearch(picker.origin, picker.destination, picker.date); setPicker(null); }}
                  className="w-full text-left rounded-xl border border-accent/40 bg-accent/5 px-3 py-2.5 flex items-center justify-between hover:bg-accent/10"
                  data-testid="discovery-airport-cheapest">
                  <span className="text-sm font-medium">Cheapest — {picker.destination_city} ({picker.destination})</span>
                  <span className="text-sm font-semibold text-accent tabular">{formatPrice(picker.price_inr, currency)}</span>
                </button>
              )}
              {airports.filter((a) => a.code !== picker?.destination).map((a) => (
                <button key={a.code} onClick={() => { runSearch(picker.origin, a.code); setPicker(null); }}
                  className="w-full text-left rounded-xl border border-border px-3 py-2.5 flex items-center gap-3 hover:bg-secondary"
                  data-testid={`discovery-airport-${a.code}`}>
                  <span className="grid place-items-center h-8 w-10 rounded-md bg-secondary text-xs font-mono font-semibold">{a.code}</span>
                  <span className="min-w-0"><span className="block text-sm font-medium truncate">{a.city || a.name}</span><span className="block text-xs text-muted-foreground truncate">{a.name}</span></span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
